/**
 * Read a generated Word document without saving it first: a preview on the page, or the file
 * opened straight in Word.
 *
 * Asked for by Craig, 1 October 2026 — a finished RIA offered only "save", and reading one
 * through meant a download, a trip to the Downloads folder and a double-click. Both routes use
 * the same presigned link the download does; neither stores anything or sends the file anywhere
 * new.
 *
 * **Open in Word** is Office's own URI scheme (`ms-word:ofe|u|<url>`). Tested against Word on
 * Craig's machine with a real RIA: Word fetched the link and opened all 34 pages. It opens
 * **read-only** — Word cannot save back to a web address — so editing is File → Save As, and the
 * button's tooltip says so. The scheme also works in Word for iOS; without Word installed the
 * browser simply has nothing to hand it to.
 *
 * **Preview** renders the .docx in the browser with `docx-preview`, loaded only when pressed (it
 * is most of a megabyte). It is a faithful rendering, not Word's own: page breaks and fine spacing
 * can differ, which the header says. Two of its options are off on purpose, because a generated
 * RIA quotes publications this project treats as untrusted input: `renderAltChunks` (embedded raw
 * HTML, rendered into an iframe) and comments. Links in the rendered document are kept only when
 * they are http(s), and open in a new tab.
 *
 * Deliberately **not** Microsoft's online viewer (view.officeapps.live.com): it works by giving
 * Microsoft the link, which is a client's working paper leaving the system.
 *
 * The bytes are fetched by script, so the exports bucket carries a CORS rule for the app's own
 * origins (`aws_s3_bucket_cors_configuration.raw`). Without it the fetch fails with no useful
 * message, which is why that failure is named below rather than shown raw.
 */

import { useEffect, useRef, useState } from "react";

import { followable } from "../links";

/** Only Word documents get these. An Excel export is a download and nothing else. */
export function isWordDocument(filename: string | undefined): boolean {
  return /\.docx$/i.test(filename ?? "");
}

/** The link that hands a document's address to Word. */
export function wordUri(url: string): string {
  return `ms-word:ofe|u|${url}`;
}

/** Preview and Open in Word, side by side, for one document. Renders nothing for non-Word files. */
export function DocumentActions({ url, filename }: { url?: string; filename?: string }) {
  const [previewing, setPreviewing] = useState(false);
  if (!url || !followable(url) || !isWordDocument(filename)) return null;
  return (
    <span className="document-actions">
      <button type="button" className="link" onClick={() => setPreviewing(true)}>
        Preview
      </button>
      <a
        href={wordUri(url)}
        title="Opens in Word as read-only. To keep an edited copy, use File → Save As."
      >
        Open in Word
      </a>
      {previewing && (
        <DocumentPreview url={url} filename={filename ?? ""} onClose={() => setPreviewing(false)} />
      )}
    </span>
  );
}

function DocumentPreview({
  url,
  filename,
  onClose,
}: {
  url: string;
  filename: string;
  onClose: () => void;
}) {
  const body = useRef<HTMLDivElement>(null);
  const styles = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<"loading" | "ready" | "failed">("loading");
  const [problem, setProblem] = useState("");

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    // The page behind should not scroll while the document is being read over it.
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
    };
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let blob: Blob;
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(
            response.status === 403
              ? "The link has expired — links last fifteen minutes. Refresh the list and try again."
              : `The document could not be fetched (${response.status}).`,
          );
        }
        blob = await response.blob();
      } catch (error) {
        if (cancelled) return;
        // A CORS refusal surfaces as a bare TypeError with no detail at all.
        setProblem(
          error instanceof TypeError
            ? "The document could not be fetched for preview. Download it or open it in Word instead."
            : error instanceof Error
              ? error.message
              : String(error),
        );
        setState("failed");
        return;
      }

      try {
        const { renderAsync } = await import("docx-preview");
        if (cancelled || !body.current) return;
        await renderAsync(blob, body.current, styles.current ?? undefined, {
          className: "docx",
          inWrapper: true,
          breakPages: true,
          ignoreLastRenderedPageBreak: true,
          renderHeaders: true,
          renderFooters: true,
          renderFootnotes: true,
          renderEndnotes: true,
          useBase64URL: true,
          // Off on purpose — see the module header.
          renderAltChunks: false,
          renderComments: false,
          renderChanges: false,
          experimental: false,
        });
        if (cancelled || !body.current) return;
        sanitiseLinks(body.current);
        fitToWidth(body.current);
        setState("ready");
      } catch (error) {
        if (cancelled) return;
        setProblem(`The document could not be displayed: ${error instanceof Error ? error.message : String(error)}`);
        setState("failed");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url]);

  // A phone is narrower than an A4 page; scale the pages down rather than scroll sideways.
  useEffect(() => {
    if (state !== "ready") return;
    const onResize = () => body.current && fitToWidth(body.current);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [state]);

  return (
    <div className="overlay doc-preview-overlay" onClick={onClose}>
      <div
        className="doc-preview"
        role="dialog"
        aria-label={`Preview of ${filename}`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="doc-preview-head">
          <div className="doc-preview-title">
            <strong>{filename}</strong>
            <span className="small muted">
              A preview — Word's own layout may differ slightly.
            </span>
          </div>
          <div className="doc-preview-links">
            <a href={wordUri(url)} title="Opens in Word as read-only. To keep an edited copy, use File → Save As.">
              Open in Word
            </a>
            <a href={url} target="_blank" rel="noreferrer noopener">
              Download
            </a>
          </div>
          <button type="button" className="doc-preview-close" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="doc-preview-body">
          {state === "loading" && <p className="muted doc-preview-note">Loading the document…</p>}
          {state === "failed" && <p className="error doc-preview-note">{problem}</p>}
          <div ref={styles} />
          <div ref={body} />
        </div>
      </div>
    </div>
  );
}

/** Keep only http(s) links, and open them away from the app. Anything else becomes plain text. */
function sanitiseLinks(root: HTMLElement) {
  root.querySelectorAll("a").forEach((anchor) => {
    const href = anchor.getAttribute("href") ?? "";
    if (href.startsWith("#")) return; // internal bookmarks within the document
    if (followable(href)) {
      anchor.target = "_blank";
      anchor.rel = "noreferrer noopener";
    } else {
      anchor.removeAttribute("href");
    }
  });
}

/** Scale the rendered pages to the space available when that is narrower than a page. */
function fitToWidth(root: HTMLElement) {
  const wrapper = root.querySelector<HTMLElement>(".docx-wrapper");
  const page = root.querySelector<HTMLElement>("section.docx");
  if (!wrapper || !page) return;
  wrapper.style.removeProperty("zoom");
  const available = root.clientWidth;
  const needed = page.offsetWidth + 32; // the wrapper's own padding either side
  const scale = Math.min(1, available / needed);
  if (scale < 1) wrapper.style.setProperty("zoom", String(scale));
}
